import type { PaceStatus } from "@/lib/countdown/legs.ts";
import styles from "./countdown.module.css";

const RADIUS = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * How much of a leg is behind you, as a ring around the percentage.
 *
 * The ring is the only part of the bar that reports the past; everything else
 * is about what's left. It takes the leg's status colour so a leg that's
 * nearly done but still doesn't fit doesn't get to look reassuring.
 */
export function ProgressRing({
  progress,
  status,
  label,
}: {
  /** 0–1. */
  progress: number;
  status: PaceStatus;
  /** What this ring is measuring, for screen readers. */
  label: string;
}) {
  const clamped = Math.min(1, Math.max(0, progress));
  const percent = Math.round(clamped * 100);

  return (
    <svg
      className={styles.ring}
      viewBox="0 0 64 64"
      data-status={status}
      role="img"
      aria-label={`${label}: ${percent}% watched`}
    >
      <circle className={styles.ringTrack} cx="32" cy="32" r={RADIUS} />
      <circle
        className={styles.ringValue}
        cx="32"
        cy="32"
        r={RADIUS}
        strokeDasharray={`${CIRCUMFERENCE * clamped} ${CIRCUMFERENCE}`}
        transform="rotate(-90 32 32)"
      />
      <text
        className={styles.ringText}
        x="32"
        y="32"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {percent}%
      </text>
    </svg>
  );
}
